import { Button, Card, CardHeader } from "@helmr/ui";
import { useMemo, useState } from "react";
import { Field, css as s } from "../../payroll/kit";
import m from "./import.module.css";
import { applyMapping, type Cell, type ColumnMapping, type Grid } from "./statement";

const textOf = (c: Cell | undefined) => (c === null || c === undefined ? "" : String(c));
const colLabel = (i: number) => String.fromCharCode(65 + (i % 26)).repeat(Math.floor(i / 26) + 1);

type Pick = { headerRow: number; date: string; narration: string; ref: string; debit: string; credit: string; amount: string; drcr: string; balance: string };
const n = (v: string) => (v === "" ? null : Number(v));

/**
 * The one-time column check for a layout we do not recognise. The choice is saved for the business under the header's
 * signature, so next month's file from the same bank skips this step.
 */
export function MappingStep({ name, grid, headerRow, guess, reason, digits, onCancel, onDone }: {
  name: string; grid: Grid; headerRow: number; signature: string; guess: Partial<ColumnMapping>; reason: string; digits: number;
  onCancel: () => void; onDone: (m: ColumnMapping) => Promise<boolean>;
}) {
  const [pick, setPick] = useState<Pick>({
    headerRow: guess.headerRow ?? headerRow,
    date: String(guess.date ?? ""), narration: String(guess.narration ?? ""), ref: String(guess.ref ?? ""), debit: String(guess.debit ?? ""),
    credit: String(guess.credit ?? ""), amount: String(guess.amount ?? ""), drcr: String(guess.drcr ?? ""), balance: String(guess.balance ?? ""),
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const width = Math.min(30, Math.max(...grid.slice(0, 80).map((r) => r.length), 1));
  const header = grid[pick.headerRow] ?? [];
  const options = Array.from({ length: width }, (_, i) => ({ i, label: `${colLabel(i)}: ${textOf(header[i]).slice(0, 28) || "(empty)"}` }));

  const mapping: ColumnMapping | null = useMemo(() => {
    if (pick.date === "" || pick.narration === "") return null;
    const split = pick.debit !== "" || pick.credit !== "";
    if (!split && pick.amount === "") return null;
    return { headerRow: pick.headerRow, date: Number(pick.date), narration: Number(pick.narration), ref: n(pick.ref), debit: n(pick.debit), credit: n(pick.credit), amount: split ? null : n(pick.amount), drcr: split ? null : n(pick.drcr), balance: n(pick.balance) };
  }, [pick]);
  const preview = useMemo(() => (mapping ? applyMapping(grid, mapping, digits) : null), [mapping, grid, digits]);

  const set = (k: keyof Pick) => (v: string) => { setErr(null); setPick((p) => ({ ...p, [k]: k === "headerRow" ? Number(v) : v })); };
  const col = (k: keyof Pick, label: string, hint: string | undefined, optional: boolean) => (
    <Field label={label} hint={hint}>
      {(a) => (
        <select {...a} className={s.input} value={String(pick[k])} onChange={(e) => set(k)(e.target.value)}>
          <option value="">{optional ? "None" : "Choose column"}</option>
          {options.map((o) => <option key={o.i} value={o.i}>{o.label}</option>)}
        </select>
      )}
    </Field>
  );

  return (
    <Card aria-label="Match the columns">
      <CardHeader title="Match the columns" subtitle={`${reason} Tell us which column is which once; we will remember it for this bank layout.`} />
      <div className={s.body}>
        <p className={s.note}>{name}</p>
        <div className={m.mapGrid}>
          <Field label="Header row" hint="The row with the column names">
            {(a) => <select {...a} className={s.input} value={pick.headerRow} onChange={(e) => set("headerRow")(e.target.value)}>{grid.slice(0, 40).map((r, i) => <option key={i} value={i}>{`Row ${i + 1}: ${r.map(textOf).filter(Boolean).join(", ").slice(0, 50) || "(empty)"}`}</option>)}</select>}
          </Field>
          {col("date", "Date", undefined, false)}
          {col("narration", "Description", "The narration or particulars", false)}
          {col("ref", "Reference", "Cheque or reference number", true)}
          {col("debit", "Debit (money out)", "Withdrawal column", true)}
          {col("credit", "Credit (money in)", "Deposit column", true)}
          {pick.debit === "" && pick.credit === "" ? <>{col("amount", "Amount", "Use this if there is one amount column", true)}{col("drcr", "Debit or credit marker", "A column saying Dr or Cr", true)}</> : null}
          {col("balance", "Balance", undefined, true)}
        </div>
        {mapping && preview ? (
          <div>
            <p className={s.note}>{preview.txns.length > 0 ? `This reads ${preview.txns.length} transactions${preview.bad.length ? ` and ${preview.bad.length} rows it cannot read` : ""}. First rows:` : "No transaction can be read with these columns yet."}</p>
            {preview.txns.length > 0 ? (
              <div className={m.preview}>
                <table aria-label="Preview">
                  <thead><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount (minor units)</th></tr></thead>
                  <tbody>{preview.txns.slice(0, 5).map((t) => <tr key={t.id}><td>{t.date}</td><td>{t.description}</td><td>{t.direction}</td><td>{t.amount}</td></tr>)}</tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
        {err ? <p className={s.formErr} role="alert">{err}</p> : null}
        <div className={s.actions}>
          <Button variant="primary" disabled={!mapping || !preview || preview.txns.length === 0} busy={busy} busyLabel="Reading…" onClick={async () => { setBusy(true); try { if (!mapping || !(await onDone(mapping))) setErr("No transaction can be read with these columns."); } finally { setBusy(false); } }}>Use these columns</Button>
          <Button onClick={onCancel}>Choose another file</Button>
        </div>
      </div>
    </Card>
  );
}
